import React from "react";
import { CheckCircle2, Thermometer, ShieldAlert, Loader2 } from "lucide-react";
import { useLang } from "@/lib/i18n";

// Customer-facing Testing & Quality Check panel.
// All strings via useLang() — direction/text follow the LangProvider set at
// the CustomerLive root, so AR/EN switches propagate automatically.
const STEP_ORDER = ["windows", "gpu_driver", "bench"];
const STEP_LABEL_KEY = {
  windows: "testing_step_windows",
  gpu_driver: "testing_step_gpu_driver",
  bench: "testing_step_bench",
};

const SEV_TOKEN = {
  success:  { text: "#00ff66", ring: "rgba(0,255,102,0.35)", bg: "rgba(0,255,102,0.05)" },
  normal:   { text: "var(--accent)", ring: "rgba(0,229,255,0.35)", bg: "rgba(0,229,255,0.05)" },
  warning:  { text: "#ff9800", ring: "rgba(255,152,0,0.4)", bg: "rgba(255,152,0,0.05)" },
  critical: { text: "#ff3333", ring: "rgba(255,51,51,0.45)", bg: "rgba(255,51,51,0.06)" },
};

const CLASSIFICATION_KEY = {
  EXCELLENT_TEMP: "testing_class_excellent",
  GOOD_TEMP: "testing_class_good",
  HIGH_TEMP: "testing_class_high",
  TEMP_TOO_HIGH: "testing_class_too_high",
};

function StepLine({ id, step, t }) {
  const status = step?.status || "PENDING";
  const done = status === "COMPLETED";
  const running = status === "IN_PROGRESS";
  const stateLabel = done ? t("testing_status_completed") :
    running ? (id === "bench" ? t("testing_status_testing") : t("testing_status_installing")) : t("testing_status_pending");
  return (
    <div className="flex items-center justify-between py-2 border-b last:border-b-0"
         style={{ borderColor: "rgba(255,255,255,0.06)" }}
         data-testid={`customer-testing-step-${id}`}>
      <span className="text-xs md:text-sm text-white/70 font-medium">{t(STEP_LABEL_KEY[id])}</span>
      <span className="text-xs font-semibold tracking-wide flex items-center gap-1.5"
            style={{ color: done ? "#00ff66" : running ? "var(--accent)" : "rgba(255,255,255,0.4)" }}
            data-testid={`customer-testing-step-${id}-status`}>
        {done ? <CheckCircle2 size={14}/> : running ? <Loader2 size={14} className="animate-spin"/> : null}
        {stateLabel}
      </span>
    </div>
  );
}

function TempCard({ kind, temp, standards, t }) {
  const expected = standards?.[kind]?.expected;
  const has = temp && temp.value !== null && temp.value !== undefined;
  const sev = SEV_TOKEN[temp?.severity] || SEV_TOKEN.normal;
  const clsKey = temp?.classification && CLASSIFICATION_KEY[temp.classification];
  return (
    <div className="rounded-lg border p-4"
         style={{ borderColor: has ? sev.ring : "rgba(255,255,255,0.08)",
                  background: has ? sev.bg : "rgba(255,255,255,0.02)" }}
         data-testid={`customer-testing-temp-${kind}`}>
      <div className="text-[10px] tracking-[0.22em] uppercase text-white/50 font-semibold flex items-center gap-1.5">
        <Thermometer size={11}/> {kind === "gpu" ? t("testing_temp_gpu") : t("testing_temp_cpu")}
      </div>
      {expected && (
        <div className="mt-0.5 text-[10px] text-white/40 tracking-wide" data-testid={`customer-testing-temp-${kind}-expected`}>
          {t("testing_expected_range")}: {expected.min}–{expected.max}°C
        </div>
      )}
      {has ? (
        <>
          <div className="mt-2 text-3xl md:text-4xl font-bold text-white" data-testid={`customer-testing-temp-${kind}-value`}>
            {temp.value}°C
          </div>
          <div className="mt-1 text-xs font-semibold tracking-wide flex items-center gap-1"
               style={{ color: sev.text }}
               data-testid={`customer-testing-temp-${kind}-class`}>
            {clsKey ? t(clsKey) : (temp.classification || "").replace(/_/g, " ")}
            {temp.severity === "success" ? " ✓" : temp.severity === "critical" ? <ShieldAlert size={12}/> : null}
          </div>
        </>
      ) : (
        <div className="mt-3 text-xs text-white/45 italic tracking-wide" data-testid={`customer-testing-temp-${kind}-waiting`}>
          {t("testing_waiting_result")}
        </div>
      )}
    </div>
  );
}

export function CustomerTestingPanel({ testing }) {
  const { t } = useLang();
  if (!testing) {
    return (
      <div className="rounded-xl border p-5" style={{ borderColor: "rgba(255,255,255,0.08)", background: "#0d0d10" }}
           data-testid="customer-testing-panel-empty">
        <div className="text-[10px] tracking-[0.28em] uppercase font-semibold" style={{ color: "var(--accent)" }}>{t("testing_title")}</div>
        <div className="mt-3 text-white/50 text-xs">{t("testing_not_started")}</div>
      </div>
    );
  }
  const standards = testing.standards_snapshot;
  return (
    <div className="rounded-xl border p-5 md:p-6 space-y-4"
         style={{ borderColor: "rgba(0,229,255,0.22)",
                  background: "linear-gradient(180deg,rgba(0,229,255,0.03),rgba(255,255,255,0.02))" }}
         data-testid="customer-testing-panel">
      <div>
        <div className="text-[10px] tracking-[0.28em] uppercase font-semibold" style={{ color: "var(--accent)" }}>{t("testing_title")}</div>
        <div className="text-white/45 text-[11px] mt-1">{t("testing_subtitle")}</div>
      </div>

      <div className="rounded-lg border p-3" style={{ borderColor: "rgba(255,255,255,0.06)" }}>
        {STEP_ORDER.map((s) => (
          <StepLine key={s} id={s} step={testing.steps?.[s]} t={t}/>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <TempCard kind="gpu" temp={testing.gpu_temp} standards={standards} t={t}/>
        <TempCard kind="cpu" temp={testing.cpu_temp} standards={standards} t={t}/>
      </div>
    </div>
  );
}
